import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { MedicalSuggestionsUpload } from "@/components/nurse/MedicalSuggestionsUpload";
import { MedicalAlertsTemplate } from "@/components/nurse/MedicalAlertsTemplate";
import { StaffBulkUpload } from "@/components/nurse/StaffBulkUpload";
import { DormitoryMotherManager } from "@/components/nurse/DormitoryMotherManager";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Header } from "@/components/layout/Header";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
const Settings = () => {
  const {
    user
  } = useAuth();
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [isStaffUploadOpen, setIsStaffUploadOpen] = useState(false);
  const [isDormitoryOpen, setIsDormitoryOpen] = useState(false);
  const {
    data: schoolData
  } = useQuery({
    queryKey: ['school', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) return null;
      const {
        data,
        error
      } = await supabase.from('schools').select('prefix').eq('id', user.schoolId).single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.schoolId
  });
  const isTISP = schoolData?.prefix === "TISP";
  return <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto p-6 pt-[160px] space-y-6">
        <h1 className="text-3xl font-bold my-[22px]">Sickbay Settings</h1>
        
        <div className="grid gap-6">
          <Card>
            <Collapsible open={isDormitoryOpen} onOpenChange={setIsDormitoryOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <CardTitle>Dormitory Mothers</CardTitle>
                      <CardDescription>
                        Manage dormitory mother assignments and view their sickbay visit statistics
                      </CardDescription>
                    </div>
                    <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform duration-200", isDormitoryOpen && "rotate-180")} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <DormitoryMotherManager />
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          <Card>
            <Collapsible open={isAlertsOpen} onOpenChange={setIsAlertsOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <CardTitle>Medical Alerts Template</CardTitle>
                      <CardDescription>
                        Select students to generate a template for updating their medical alerts
                      </CardDescription>
                    </div>
                    <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform duration-200", isAlertsOpen && "rotate-180")} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <MedicalAlertsTemplate />
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
          
          <Card>
            <Collapsible open={isSuggestionsOpen} onOpenChange={setIsSuggestionsOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <CardTitle>Medical Suggestions</CardTitle>
                      <CardDescription>
                        Upload medical suggestions from an Excel file. The file should have three columns: COMPLAINT, DIAGNOSIS, and TREATMENT.
                      </CardDescription>
                    </div>
                    <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform duration-200", isSuggestionsOpen && "rotate-180")} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <MedicalSuggestionsUpload />
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          <Card>
            <Collapsible open={isStaffUploadOpen} onOpenChange={setIsStaffUploadOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <CardTitle>Staff Bulk Upload</CardTitle>
                      <CardDescription>
                        Upload multiple staff members from an Excel file. The file should have NAME and CATEGORY columns.
                      </CardDescription>
                    </div>
                    <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform duration-200", isStaffUploadOpen && "rotate-180")} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <StaffBulkUpload />
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </div>
      </main>
    </div>;
};
export default Settings;